/**
 * Wishlist ("Merkliste") panel.
 *
 * A yellow slide-out pinned to the right edge (2em peek → 20vw open, desktop-only).
 * Lists the events the visitor hearted on the big calendar in the calendar-teaser
 * format and exports them as one .ics ("Deinem Kalender hinzufügen").
 *
 * localStorage (wishlistStore) is a cache of snapshots; on load and each open we
 * reconcile against live data via glunz/v1/event-dates/lookup so a later
 * cancellation / sold-out / date move is reflected instead of showing stale text.
 *
 * Visibility: the calendar page only — the panel belongs to the big calendar and
 * has no purpose elsewhere. `[data-calendar]` (rendered by detail/calendar.php) is
 * how we recognise that page. The panel is rendered on every page from footer.php
 * and stays `.is-hidden` unless the calendar is there. Hidden below 768px too (the
 * big calendar is desktop-only).
 */

import moment from './calendarMoment.js'
import { lang } from './calendarMoment.js'
import { element, parseArtistNames, eventDisplayTitle, resolveBooking } from './calendarHelpers.js'
import wishlistStore, { keyFor } from './wishlistStore.js'

const panel = document.querySelector('[data-wishlist]')

if (panel && document.querySelector('[data-calendar]')) {
  const restUrl = window.php_vars?.restUrl
  const labels = (() => {
    try { return JSON.parse(panel.dataset.i18n || '{}') } catch { return {} }
  })()

  const handle    = panel.querySelector('[data-wishlist-toggle]')
  const list      = panel.querySelector('[data-wishlist-list]')
  const emptyEl   = panel.querySelector('[data-wishlist-empty]')
  const exportBtn = panel.querySelector('[data-wishlist-export]')

  panel.classList.remove('is-hidden')

  // DB tinyints arrive as "0"/"1" over REST.
  const isTrue = (v) => v === 1 || v === '1' || v === true

  // ── Formatting ─────────────────────────────────────────────────────────────
  const formatDate = (dateStart) => moment(dateStart).format('dddd, D.M.YYYY') // "Samstag, 14.5.2026"

  const formatTime = (item) => {
    const start = moment(item.dateStart)
    const end = item.dateEnd ? moment(item.dateEnd) : null
    return end && !end.isSame(start)
      ? `${start.format('HH:mm')} – ${end.format('HH:mm')}`
      : start.format('HH:mm')
  }

  // Shared with the mini calendar and mirrored from Repository::booking() — the
  // editor's label wins, else one derived from the registration type; the href
  // rules for `tickets_soon` / `info` live in calendarHelpers.js. This is a
  // teaser context, so `info` falls back to the event permalink. Labels come
  // from the panel's data-i18n (localised in the page language).
  const booking = (item) => resolveBooking(item, labels, item.permalink)

  // The one status/registration line as plain text (used for the .ics description
  // and the cancelled/sold-out lines).
  const statusText = (item) => {
    if (item.status === 'cancelled') return labels.cancelled || ''
    if (item.status === 'sold_out') {
      return item.soldOutText
        || (item.soldOutType === 'booked_out' ? labels.bookedOut : labels.soldOut)
        || ''
    }
    return booking(item).label
  }

  // The status line as a DOM node, mirroring the mini-calendar teaser
  // (calendarOverlay.js statusNode): a ticket/booking link when there's a URL,
  // plain registration text otherwise. Cancelled/sold-out show status text only
  // (notice-coloured via CSS). Moved/removed dates link to the event's current dates.
  const statusNode = (item) => {
    if (item.status === 'changed') {
      const text = labels.changed || 'Termin geändert'
      const tag = item.permalink ? 'a' : 'div'
      const el = element(tag, `wishlist-item-status wishlist-item-changed${item.permalink ? ' text-link button-accessible' : ''}`,
        item.permalink ? { href: item.permalink } : {})
      el.textContent = text
      return el
    }
    if (item.status === 'cancelled' || item.status === 'sold_out') {
      const s = element('div', 'wishlist-item-status')
      s.textContent = statusText(item)
      return s
    }
    // available: ticket/booking link ("as other places"), else registration text.
    const { href: url, label, external } = booking(item)
    if (url) {
      const attrs = external
        ? { href: url, target: '_blank', rel: 'noopener noreferrer' }
        : { href: url }
      const a = element('a', 'wishlist-item-status text-link button-accessible', attrs)
      a.textContent = label || labels.book || ''
      if (external && labels.newWindow) {
        const hidden = element('span', 'hidden')
        hidden.textContent = ` ${labels.newWindow}`
        a.appendChild(hidden)
      }
      return a
    }
    if (label) {
      const s = element('div', 'wishlist-item-status')
      s.textContent = label
      return s
    }
    return null
  }

  // ── Render ───────────────────────────────────────────────────────────────
  const renderItem = (item) => {
    const li = element('li', `wishlist-item is-${item.status}`, { 'data-wishlist-key': item.key })

    const date = element('div', 'wishlist-item-date')
    date.textContent = formatDate(item.dateStart)
    li.appendChild(date)

    const time = element('div', 'wishlist-item-time')
    time.textContent = formatTime(item)
    li.appendChild(time)

    // Title links to the event detail page.
    const title = element(
      item.permalink ? 'a' : 'div',
      `wishlist-item-title${item.permalink ? ' text-link button-accessible' : ''}`,
      item.permalink ? { href: item.permalink } : {},
    )
    title.textContent = item.title || item.postTitle || ''
    li.appendChild(title)

    if (item.artists) {
      const artists = element('div', 'wishlist-item-artists')
      artists.textContent = item.artists
      li.appendChild(artists)
    }

    const status = statusNode(item)
    if (status) li.appendChild(status)

    const remove = element('button', 'wishlist-item-remove button-accessible', {
      type: 'button', 'aria-label': labels.remove || 'Entfernen',
    })
    remove.textContent = '×'
    remove.addEventListener('click', () => wishlistStore.remove(item.key))
    li.appendChild(remove)

    return li
  }

  const render = () => {
    const items = wishlistStore.getItems()
    list.replaceChildren(...items.map(renderItem))
    if (emptyEl) emptyEl.hidden = items.length > 0
    if (exportBtn) exportBtn.hidden = !items.some((i) => i.status !== 'changed')
  }

  // Keep every calendar heart's filled state in step with the store (an item
  // removed from the panel un-fills its heart; cross-tab changes propagate).
  const syncCalendarHearts = () => {
    document.querySelectorAll('[data-wishlist-key]').forEach((btn) => {
      if (btn.tagName !== 'BUTTON' || !btn.classList.contains('calendar-event-wishlist')) return
      const saved = wishlistStore.has(btn.dataset.wishlistKey)
      btn.classList.toggle('is-wishlisted', saved)
      btn.setAttribute('aria-pressed', String(saved))
    })
  }

  // ── Open / close (lightweight peek — no backdrop, no scroll lock) ──────────
  const isOpen = () => panel.classList.contains('is-open')

  function setOpen(open) {
    panel.classList.toggle('is-open', open)
    if (handle) handle.setAttribute('aria-expanded', String(open))
    if (open) reconcile()
  }

  if (handle) handle.addEventListener('click', () => setOpen(!isOpen()))

  // Clicking the open panel closes it — the closing heart, the title, any bare
  // yellow. Links and buttons (item titles, ticket links, ×, export) are the
  // exception: they do their own job and leave the panel open.
  panel.addEventListener('click', (e) => {
    if (!isOpen()) return
    if (e.target.closest('[data-wishlist-close]')) return setOpen(false)
    if (e.target.closest('a, button')) return
    setOpen(false)
  })

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) setOpen(false)
  })

  // Outside click. composedPath() (not panel.contains) because a handler earlier in
  // the same dispatch may already have detached the target — removing an item
  // re-renders the list, so its × button is gone by the time this runs and would
  // otherwise read as a click outside the panel and close it.
  document.addEventListener('click', (e) => {
    if (!e.isTrusted) return // ignore synthetic clicks (e.g. the .ics download anchor)
    if (!isOpen()) return
    if (e.composedPath().includes(panel)) return
    setOpen(false)
  })

  // Hearting an event in the big calendar opens the panel so the visitor sees where
  // it landed (calendarHelpers.js fires this; a plain event keeps the store pure and
  // avoids an import cycle).
  document.addEventListener('wishlist:added', () => setOpen(true))

  // ── Reconcile against live data ────────────────────────────────────────────
  const reconcile = async () => {
    const items = wishlistStore.getItems()
    if (!items.length || !restUrl) return
    const ids = [...new Set(items.map((i) => i.postId))]

    let rows = []
    try {
      const params = new URLSearchParams({ post_ids: ids.join(','), lang })
      const r = await fetch(`${restUrl}glunz/v1/event-dates/lookup?${params}`)
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const data = await r.json()
      rows = Array.isArray(data?.dates) ? data.dates : []
    } catch {
      return // Keep last-known snapshots silently on failure.
    }

    const freshByKey = new Map(rows.map((row) => [keyFor(row), row]))
    const rowsByPost = new Map()
    for (const row of rows) {
      const pid = String(row.post_id)
      if (!rowsByPost.has(pid)) rowsByPost.set(pid, [])
      rowsByPost.get(pid).push(row)
    }

    // Re-read (not the snapshot captured above) so a toggle during the fetch isn't clobbered.
    const reconciled = wishlistStore.getItems().map((item) => {
      const fresh = freshByKey.get(item.key)
      if (!fresh) return { ...item, status: 'changed' } // moved or removed occurrence

      const day = String(fresh.date_start).slice(0, 10)
      const sameDay = (rowsByPost.get(String(fresh.post_id)) || [])
        .filter((r) => String(r.date_start).slice(0, 10) === day)

      return {
        ...item,
        dateStart: fresh.date_start,
        dateEnd: fresh.date_end || null,
        postTitle: fresh.post_title || '',
        semesterTitle: fresh.semester_title || '',
        title: eventDisplayTitle(fresh, sameDay),
        artists: parseArtistNames(fresh.artist_names).join(', '),
        permalink: fresh.permalink || item.permalink || '',
        registrationType: fresh.registration_type || '',
        ticketTitle: fresh.ticket_title || '',
        ticketUrl: fresh.ticket_url || '',
        isCancelled: fresh.is_cancelled,
        isSoldOut: fresh.is_sold_out,
        soldOutText: fresh.sold_out_text || '',
        soldOutType: fresh.sold_out_type || '',
        status: isTrue(fresh.is_cancelled) ? 'cancelled'
          : (isTrue(fresh.is_sold_out) ? 'sold_out' : 'available'),
      }
    })

    wishlistStore.setItems(reconciled) // notifies → render()
  }

  // ── .ics export (client-side, Europe/Zurich → UTC) ─────────────────────────
  const zurich = (dt) => moment.tz(dt, 'YYYY-MM-DD HH:mm:ss', 'Europe/Zurich')
  const icsUtc = (m) => m.clone().utc().format('YYYYMMDD[T]HHmmss[Z]')
  const icsEscape = (s) => String(s || '')
    .replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')

  // RFC 5545 line folding: split lines longer than ~75 chars, continuations start
  // with a single space. Approximated by chars (our content is Latin-ish).
  const fold = (line) => {
    if (line.length <= 73) return line
    let out = ''
    let rest = line
    while (rest.length > 73) { out += rest.slice(0, 73) + '\r\n '; rest = rest.slice(73) }
    return out + rest
  }

  const buildIcs = (items) => {
    const now = moment().utc().format('YYYYMMDD[T]HHmmss[Z]')
    const lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Tanzhaus//Merkliste//DE',
      'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    ]
    for (const item of items) {
      const start = icsUtc(zurich(item.dateStart))
      const end = icsUtc(item.dateEnd ? zurich(item.dateEnd) : zurich(item.dateStart).add(1, 'hour'))
      const desc = []
      if (item.artists) desc.push(item.artists)
      const st = statusText(item)
      if (st) desc.push(st)
      if (item.permalink) desc.push(item.permalink)

      lines.push('BEGIN:VEVENT')
      lines.push(`UID:${icsEscape(item.key)}@tanzhaus`)
      lines.push(`DTSTAMP:${now}`)
      lines.push(`DTSTART:${start}`)
      lines.push(`DTEND:${end}`)
      lines.push(fold(`SUMMARY:${icsEscape(item.title || item.postTitle)}`))
      if (desc.length) lines.push(fold(`DESCRIPTION:${icsEscape(desc.join('\n'))}`))
      if (item.permalink) lines.push(fold(`URL:${item.permalink}`))
      lines.push('LOCATION:Tanzhaus Zürich')
      if (item.status === 'cancelled') lines.push('STATUS:CANCELLED')
      lines.push('END:VEVENT')
    }
    lines.push('END:VCALENDAR')
    return lines.join('\r\n')
  }

  const exportIcs = () => {
    const items = wishlistStore.getItems().filter((i) => i.status !== 'changed')
    if (!items.length) return
    const blob = new Blob([buildIcs(items)], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'wunschliste.ics'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  if (exportBtn) exportBtn.addEventListener('click', exportIcs)

  // ── Boot ───────────────────────────────────────────────────────────────────
  wishlistStore.subscribe(() => { render(); syncCalendarHearts() })
  render()
  reconcile()
}
